/**
 * DocumentTileGrid — the universal document-capture tile grid (Contract C3).
 *
 * The single capture entry point Bill wants app-wide (brief §2.1): tiles are
 * the ONLY door to capture; clicking or dropping a file on a tile opens the
 * one CaptureModal. Replaces the three competing inline surfaces (CIP scan
 * panel, NRA tabs, Document-Requirements upload buttons) with one grid.
 *
 * Self-contained + prop-driven (C3): BR mounts it in IndividualReviewBody, the
 * PdwShell Stage-4, and the entity dialogs. It composes the presentational
 * `DocsToCaptureGrid` (locked tile-display spec — icon · name · one-line
 * description · REQUIRED/OPTIONAL badge · ✓ Captured · filename · drop hint)
 * for rendering and `CaptureModal` for capture, owns captured-tile state, and
 * emits results via callbacks. It does NOT write `person.extra` — per C2 it
 * emits `AttachedDoc[]` via `onAttachedDocsChange` and BR persists them.
 *
 * Extraction routing (which Azure DI model a tile uses) is cohort/doc-specific
 * and owned by the parent: the grid calls `onScan(tileId, base64, mime, type)`
 * and the parent dispatches to the right endpoint. `no-extraction` tiles (e.g.
 * the H-1B I-797 manual tile) skip the DI pass and attach directly.
 */
import { useCallback, useMemo, useState } from 'react';
import { useConfirm } from '@/components/primitives';
import {
  DocsToCaptureGrid,
  type DocTileSpec,
  type ExtractedFieldRow,
  type TileGatingLevel,
} from '../DocsToCaptureGrid';
import { CaptureModal, type ScanResultPayload, type CaptureArtifact, type OverrideOption } from './CaptureModal';
import type { AttachedDoc } from '../shared/attachedDocs';
import { estimateBase64Size } from '../shared/attachedDocs';
import {
  detectCrossDocConflicts,
  type CapturedDocForConflict,
  type ConflictRule,
  type ConflictFinding,
} from './detectCrossDocConflicts';

/** One tile in the universal grid (C3 `tiles: DocTile[]`). */
export interface DocTile {
  tileId: string;
  label: string;
  /** One-line description shown on the tile body (locked tile-display spec). */
  description: string;
  iconSlug: string;
  /** REQUIRED vs OPTIONAL badge. */
  required: boolean;
  /** Doc-type value used for extraction routing + the override-dropdown default. */
  docTypeValue?: string;
  /** Skip the Azure DI pass — attach only (e.g. H-1B I-797 manual tile). */
  noExtraction?: boolean;
  /** Optional starting capture state (Edit-mode hydration). */
  capturedFilename?: string;
  capturedPageCount?: number;
  extractedFields?: ReadonlyArray<ExtractedFieldRow>;
  /**
   * B2 (Bill 2026-06-03) — optional hydrated preview for an already-captured
   * doc (e.g. an existing CIF's prior upload lazy-fetched from ao_Documents).
   * In-session captures populate this from the produced PDF artifact instead.
   * Drives the QuickPreviewDrawer `<img>` / PdfCanvasViewer render.
   */
  thumbnailDataUrl?: string;
  thumbnailMimeType?: string;
}

/** Everything a single tile capture produced — handed to `onCapture`. */
export interface CaptureResult {
  scanResult: ScanResultPayload;
  attachedDoc: AttachedDoc;
  artifact: CaptureArtifact;
  chosenDocType?: string;
}

export interface DocumentTileGridProps {
  tiles: ReadonlyArray<DocTile>;
  /**
   * Extraction dispatch — parent routes to the correct Azure DI model for the
   * tile/doc-type. `no-extraction` tiles never call this. Return a successful
   * empty-fields payload to attach without auto-fill.
   */
  onScan: (tileId: string, base64: string, mimeType: string, docTypeValue?: string) => Promise<ScanResultPayload>;
  /** Post-Apply notification — parent patches person fields from the result. */
  onCapture?: (tileId: string, result: CaptureResult) => void;
  /** C2 — emits the full AttachedDoc set on every capture. BR persists it. */
  onAttachedDocsChange?: (docs: ReadonlyArray<AttachedDoc>) => void;
  /**
   * Fired after a captured tile is deleted (post-confirm). Lets the parent
   * clear the person fields that capture had auto-filled (e.g. the NRA scanner
   * nulls its doc-specific slots).
   */
  onTileDelete?: (tileId: string) => void;
  /**
   * C4 — fires with the cross-doc conflict findings recomputed after each
   * capture. BR surfaces major→red ALERT (contributing a `fail` to
   * verifyClearance.idScan) and minor→FieldComparisonDialog "pick". Empty
   * until two extractable docs are captured.
   */
  onConflictsChange?: (findings: ReadonlyArray<ConflictFinding>) => void;
  /** Active conflict rules from /v3/config/conflict-rules (C5). Default catalog used when omitted. */
  conflictRules?: ReadonlyArray<ConflictRule>;
  /** Cohort key (carried for telemetry / future per-cohort behaviour). */
  cohort?: string;
  mode?: 'add' | 'edit';
  /** Optional "Detected as …" override dropdown options (generic flows). */
  overrideOptions?: ReadonlyArray<OverrideOption>;
  /** Suggests a detected type from the bytes (salvaged 2-pass classifier). */
  autoClassify?: (base64: string, mimeType: string) => Promise<{ docTypeValue: string; detectedRawType?: string }>;
  readOnly?: boolean;
  maxUploadBytes?: number;
  /** Continue/Return footer dyad (optional — outer shells may own their own). */
  onContinue?: () => void;
  onReturn?: () => void;
}

interface ModalTarget {
  tile: DocTile;
  initialFile: File | null;
}

function gating(required: boolean): TileGatingLevel {
  return required ? 'REQUIRED' : 'OPTIONAL';
}

/**
 * Reduce a tile's generic extracted-field map to the comparable shape the C4
 * comparator understands. Tolerates the differing key names the Azure DI models
 * return (e.g. foreignPassportNumber vs passportNumber, addressLine1 vs addr).
 */
function toConflictDoc(
  tileId: string,
  label: string,
  fields: Record<string, { value: string | null; confidence: number }> | undefined,
): CapturedDocForConflict {
  const g = (...keys: string[]): string | undefined => {
    for (const k of keys) {
      const v = fields?.[k]?.value;
      if (v != null && v !== '') return v;
    }
    return undefined;
  };
  return {
    docId: tileId,
    label,
    firstName: g('firstName'),
    middleName: g('middleName'),
    lastName: g('lastName'),
    dob: g('dateOfBirth', 'dob'),
    passportNumber: g('foreignPassportNumber', 'passportNumber'),
    idNumber: g('idNumber', 'documentNumber'),
    addr: g('addr', 'addressLine1', 'address'),
    city: g('city', 'addressCity'),
    state: g('state', 'addressState'),
    zip: g('zip', 'addressZip'),
    gender: g('gender', 'sex'),
  };
}

export function DocumentTileGrid({
  tiles,
  onScan,
  onCapture,
  onAttachedDocsChange,
  onTileDelete,
  onConflictsChange,
  conflictRules,
  cohort,
  mode = 'add',
  overrideOptions,
  autoClassify,
  readOnly = false,
  maxUploadBytes,
  onContinue,
  onReturn,
}: DocumentTileGridProps): JSX.Element {
  // BACKLOG-1297 — in-app remove confirm (Teams no-ops window.confirm).
  const confirm = useConfirm();
  // Per-tile captured state, keyed by tileId. B2 — carries an optional preview
  // (thumbnailDataUrl/Mime) for the QuickPreviewDrawer.
  type CapturedTileState = {
    filename: string;
    pageCount: number;
    fields: ReadonlyArray<ExtractedFieldRow>;
    thumbnailDataUrl?: string;
    thumbnailMimeType?: string;
  };
  const [captured, setCaptured] = useState<Record<string, CapturedTileState>>(
    () => {
      const seed: Record<string, CapturedTileState> = {};
      for (const t of tiles) {
        if (t.capturedFilename) {
          seed[t.tileId] = {
            filename: t.capturedFilename,
            pageCount: t.capturedPageCount ?? 1,
            fields: t.extractedFields ?? [],
            ...(t.thumbnailDataUrl ? { thumbnailDataUrl: t.thumbnailDataUrl } : {}),
            ...(t.thumbnailMimeType ? { thumbnailMimeType: t.thumbnailMimeType } : {}),
          };
        }
      }
      return seed;
    },
  );
  const [docs, setDocs] = useState<AttachedDoc[]>([]);
  const [target, setTarget] = useState<ModalTarget | null>(null);
  const [previewTileId, setPreviewTileId] = useState<string | null>(null);
  // C4 — per-tile comparable field snapshots; recomputed into findings on every
  // capture/delete so BR can surface conflicts (major→ALERT, minor→pick).
  const [conflictDocs, setConflictDocs] = useState<Record<string, CapturedDocForConflict>>({});

  const tileSpecs: ReadonlyArray<DocTileSpec> = useMemo(
    () =>
      tiles.map((t) => {
        const cap = captured[t.tileId];
        // B2 — prefer the in-session capture preview; fall back to a hydrated
        // thumbnail the parent supplied (existing-CIF reopen lazy-fetch).
        const thumbUrl = cap?.thumbnailDataUrl ?? t.thumbnailDataUrl;
        const thumbMime = cap?.thumbnailMimeType ?? t.thumbnailMimeType;
        const spec: DocTileSpec = {
          tileId: t.tileId,
          label: t.label,
          hint: t.description,
          iconSlug: t.iconSlug,
          gatingLevel: gating(t.required),
          status: cap ? 'captured' : 'idle',
          ...(cap?.filename ? { filename: cap.filename } : {}),
          ...(cap?.pageCount ? { pageCount: cap.pageCount } : {}),
          ...(cap && cap.fields.length > 0 ? { extractedFields: cap.fields, fieldsRead: cap.fields.length } : {}),
          ...(thumbUrl ? { thumbnailDataUrl: thumbUrl } : {}),
          ...(thumbMime ? { thumbnailMimeType: thumbMime } : {}),
        };
        return spec;
      }),
    [tiles, captured],
  );

  const openModal = useCallback(
    (tileId: string, initialFile: File | null): void => {
      if (readOnly) return;
      const tile = tiles.find((t) => t.tileId === tileId);
      if (!tile) return;
      setPreviewTileId(null);
      setTarget({ tile, initialFile });
    },
    [tiles, readOnly],
  );

  const handleTileClick = useCallback(
    (tileId: string): void => {
      // Captured tile → quick-preview drawer toggle; otherwise open capture.
      if (captured[tileId]) {
        setPreviewTileId((prev) => (prev === tileId ? null : tileId));
        return;
      }
      openModal(tileId, null);
    },
    [captured, openModal],
  );

  // Extraction dispatch for the active tile (no-extraction short-circuits).
  const handleModalCapture = useCallback(
    async (base64: string, mimeType: string, docTypeValue?: string): Promise<ScanResultPayload> => {
      if (!target) return { success: false, error: 'No active tile' };
      if (target.tile.noExtraction) {
        return { success: true, fields: {}, needsConfirm: [] };
      }
      return onScan(target.tile.tileId, base64, mimeType, docTypeValue ?? target.tile.docTypeValue);
    },
    [target, onScan],
  );

  const handleModalApply = useCallback(
    (result: ScanResultPayload, artifact: CaptureArtifact, chosenDocType?: string): void => {
      if (!target) return;
      const tile = target.tile;
      const filename = artifact.originalFilename ?? `${tile.tileId}-${Date.now()}.pdf`;
      const fields: ExtractedFieldRow[] = result.fields
        ? Object.entries(result.fields)
            .filter(([, v]) => v && v.value != null && v.value !== '')
            .map(([k, v]) => ({ key: k, value: String(v.value), confidence: v.confidence }))
        : [];

      // C2 — build the AttachedDoc from the PDF artifact + emit upward. We do
      // NOT write person.extra (BR persists attachedDocs).
      const attachedDoc: AttachedDoc = {
        docCode: tile.tileId,
        source: 'cip-scan',
        filename,
        contentType: artifact.pdfMimeType,
        sizeBytes: estimateBase64Size(artifact.pdfBase64),
        imageDataUrl: '', // stored artifact is a PDF; thumbnail rendered from icon
        fileUrl: '',
        capturedAt: new Date().toISOString(),
      };

      setDocs((prev) => {
        const next = prev.filter((d) => d.docCode !== attachedDoc.docCode).concat(attachedDoc);
        onAttachedDocsChange?.(next);
        return next;
      });
      // B2 (Bill 2026-06-03): retain the produced PDF as an in-session preview
      // so the QuickPreviewDrawer renders the actual captured document (iframe)
      // instead of an icon. Held in component state only — NOT persisted onto
      // the AttachedDoc (peopleJson stays light; reopen re-fetches from SP).
      setCaptured((prev) => ({
        ...prev,
        [tile.tileId]: {
          filename,
          pageCount: artifact.pageCount,
          fields,
          thumbnailDataUrl: `data:${artifact.pdfMimeType};base64,${artifact.pdfBase64}`,
          thumbnailMimeType: artifact.pdfMimeType,
        },
      }));

      // C4 — fold this capture's comparable fields in + recompute cross-doc
      // conflicts; emit findings upward for BR to surface.
      setConflictDocs((prev) => {
        const next = { ...prev, [tile.tileId]: toConflictDoc(tile.tileId, tile.label, result.fields) };
        if (onConflictsChange) {
          onConflictsChange(detectCrossDocConflicts(Object.values(next), conflictRules));
        }
        return next;
      });

      onCapture?.(tile.tileId, { scanResult: result, attachedDoc, artifact, ...(chosenDocType ? { chosenDocType } : {}) });

      // Telemetry — capture without breaking the flow.
      try {
        // eslint-disable-next-line no-console
        console.info('[audit]', 'pdw.tilegrid.tile.captured', {
          tileId: tile.tileId,
          ...(cohort ? { cohort } : {}),
          pageCount: artifact.pageCount,
        });
      } catch { /* swallow */ }

      setTarget(null);
    },
    [target, cohort, onCapture, onAttachedDocsChange, onConflictsChange, conflictRules],
  );

  const handlePreviewDelete = useCallback(
    async (tileId: string): Promise<void> => {
      const tile = tiles.find((t) => t.tileId === tileId);
      // BACKLOG-1297 — in-app confirm; the native one is suppressed in Teams,
      // so the FSR got no question before a capture was dropped.
      const ok = await confirm({
        title: 'Remove this capture?',
        message: `The extracted values from the ${tile?.label ?? tileId} clear from this person. `
          + 'The scan audit row is retained per 7-yr retention policy.',
        confirmLabel: 'Remove capture',
        cancelLabel: 'Keep it',
        tone: 'danger',
      });
      if (!ok) return;
      setCaptured((prev) => {
        const next = { ...prev };
        delete next[tileId];
        return next;
      });
      setDocs((prev) => {
        const next = prev.filter((d) => d.docCode !== tileId);
        onAttachedDocsChange?.(next);
        return next;
      });
      // C4 — drop the deleted doc from the conflict set + re-emit.
      setConflictDocs((prev) => {
        const next = { ...prev };
        delete next[tileId];
        if (onConflictsChange) {
          onConflictsChange(detectCrossDocConflicts(Object.values(next), conflictRules));
        }
        return next;
      });
      setPreviewTileId(null);
      onTileDelete?.(tileId);
    },
    [tiles, onAttachedDocsChange, onTileDelete, onConflictsChange, conflictRules, confirm],
  );

  return (
    <div className="pdw-tilegrid">
      <DocsToCaptureGrid
        tiles={tileSpecs}
        onTileClick={handleTileClick}
        onTileDrop={(tileId, file) => openModal(tileId, file)}
        previewTileId={previewTileId}
        onPreviewClose={() => setPreviewTileId(null)}
        onPreviewRescan={(tileId) => openModal(tileId, null)}
        onPreviewDelete={handlePreviewDelete}
        {...(onContinue ? { onContinue } : {})}
        {...(onReturn ? { onReturn } : {})}
      />

      {target && (
        <CaptureModal
          open={target !== null}
          docLabel={target.tile.label}
          docHint={target.tile.description}
          iconSlug={target.tile.iconSlug}
          mode={mode}
          initialFile={target.initialFile}
          {...(overrideOptions ? { overrideOptions } : {})}
          {...(autoClassify ? { autoClassify } : {})}
          onCapture={handleModalCapture}
          onApply={handleModalApply}
          onClose={() => setTarget(null)}
          {...(maxUploadBytes ? { maxUploadBytes } : {})}
        />
      )}
    </div>
  );
}
